import { Collection, Database, Q } from '@nozbe/watermelondb';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import Organisation from './Organisation';
import OrganisationUser from './OrganisationUser';
import Region from './Region';
import { RegionModel } from '../../types/Region';
import { UserModel, UserPayload } from '../../types/User';
import { DBServiceOptionsWithImages } from '../../types/dbService';
import { DatabaseValidation } from '../../types/validation';
import {
    addPreparedDataBatch,
    mapRecord,
    PreparedUserData,
    prepareUserLocation,
} from './helpers/user/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logUpdateUserAction,
    prepareSetUserProfiling,
    prepareUpdateUser,
    prepareUpdateUserField,
} from './helpers/user/userUpdate';

type UserServiceOptions = DBServiceOptionsWithImages & {
    validation: DatabaseValidation;
};

class User {
    private database: Database;
    private collection: Collection<UserModel>;
    private table = 'users';
    private options: UserServiceOptions;

    constructor(options: UserServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    get() {
        return this.collection.query();
    }

    getById(id: string) {
        return this.collection.query(Q.where('id', id));
    }

    getByEntryId(id: string) {
        return this.collection.query(Q.on('entry_users', 'entry_id', id));
    }

    async getColorById(user_id: string): Promise<string> {
        const organisationUserService = new OrganisationUser(this.database);
        const color = await organisationUserService.getColorByUserId(user_id);

        return color[0]?.code;
    }

    async getUserRegion(userId: string): Promise<RegionModel | undefined> {
        const regionService = new Region(this.database);

        const user = await this.getById(userId).fetch();
        // MSTD-8086: the user row may not be present yet (e.g. during signup),
        // so guard against an empty fetch / missing region instead of crashing
        // on `user[0].region` (TypeError: Cannot read property 'region').
        const region = user[0]?.region;
        if (!region) return undefined;

        const regions = await regionService.getByParam('code', region).fetch();

        return regions[0];
    }

    getActiveUsers() {
        return this.collection.query(
            Q.on('organisation_users', Q.where('active', true)),
        );
    }

    async update(userId: string, payload: UserPayload) {
        const { image, avatar, removeAvatar, location } = payload;
        const userElement = await this.getById(userId).fetch();

        if (!userElement[0]) {
            throw new Error(
                `(User) Cannot update user ${userId}: no local record found`,
            );
        }

        const { id: userID } = userElement[0];
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });
        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        const updatedUser = await this.database.write(async () => {
            const preparedData: PreparedUserData[] = [];
            const { preparedLocation, existingLocation } =
                await prepareUserLocation({
                    location,
                    userId,
                    organisationID,
                    database: this.database,
                });
            const locationElement = preparedLocation ?? existingLocation;

            if (preparedLocation) {
                preparedData.push(
                    mapRecord(preparedLocation, 'locations', 'add'),
                );
            }

            const updatedUserElement = prepareUpdateUser({
                userElement: userElement[0],
                payload,
                locationElement,
            });
            preparedData.push(mapRecord(updatedUserElement, 'users', 'update'));

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateUserAction({
                user: updatedUserElement,
                logDBAction: this.options.logDBAction,
                message: 'Update user',
                tableName: this.table,
            });

            return updatedUserElement;
        });

        if (removeAvatar) {
            await this.options.imageService.remove(userID);
        }

        if (image && image.uri) {
            this.options.imageService.uploadImage({
                image,
                entityID: userID,
                entityType: 'User',
                annotationImage: '',
                ownerID: userID,
                userIDs: [userID],
                documentID: avatar?.documentID,
                organisationID,
            });
        }

        return updatedUser;
    }

    async updateField(
        userId: string,
        fieldName: string,
        fieldValue: string | number | boolean,
    ) {
        const userElement = await this.getById(userId).fetch();

        await this.database.write(async () => {
            const preparedData: PreparedUserData[] = [];
            const updatedUser = prepareUpdateUserField({
                userElement: userElement[0],
                fieldName,
                fieldValue,
            });
            preparedData.push(mapRecord(updatedUser, 'users', 'update'));

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateUserAction({
                user: updatedUser,
                logDBAction: this.options.logDBAction,
                message: `Update user field ${fieldName}`,
                tableName: this.table,
            });
        });
    }

    companyName(userID: string): Observable<string> {
        return this.getById(userID)
            .observeWithColumns(['company_name'])
            .pipe(map(([u]) => u?.companyName ?? ''));
    }

    isTeamCreated(userID: string): Observable<boolean> {
        return this.getById(userID)
            .observeWithColumns(['is_team_created'])
            .pipe(map(([u]) => u?.isTeamCreated ?? false));
    }

    menuOrder(userID: string): Observable<string> {
        return this.getById(userID)
            .observeWithColumns(['menu_order'])
            .pipe(map(([u]) => u?.menuOrder ?? ''));
    }

    async setProfiling(userId: string, value: boolean) {
        const userElement = await this.getById(userId).fetch();

        let traceID = uuidv4();

        if (!value) {
            traceID = '';
        }

        await this.database.write(async () => {
            const preparedData: PreparedUserData[] = [];
            const updatedUser = prepareSetUserProfiling({
                userElement: userElement[0],
                value,
                traceID,
            });
            preparedData.push(mapRecord(updatedUser, 'users', 'update'));

            validateEntityRecords(preparedData, this.options.validation);
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateUserAction({
                user: updatedUser,
                logDBAction: this.options.logDBAction,
                message: 'User set profiling',
                tableName: this.table,
            });
        });
    }
}

export default User;
